import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firebaseAuth, firebaseDb } from '../firebase';

export const chatService = {
  /**
   * Créer ou récupérer une conversation
   */
  getOrCreateConversation: async (orderId, agentId) => {
    try {
      if (!firebaseAuth?.currentUser) {
        return { success: false, error: 'Non authentifié' };
      }
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }

      const uid = firebaseAuth.currentUser.uid;
      const participantUids = [uid, ...(agentId ? [agentId] : [])];

      const q = query(
        collection(firebaseDb, 'conversations'),
        where('orderId', '==', orderId || null),
        where('participantUids', 'array-contains', uid),
        limit(1)
      );
      const existing = await getDocs(q);
      if (!existing.empty) {
        const d = existing.docs[0];
        return { success: true, conversation: { id: d.id, _id: d.id, ...d.data() } };
      }

      const ref = await addDoc(collection(firebaseDb, 'conversations'), {
        orderId: orderId || null,
        participantUids,
        status: 'open',
        lastMessageAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const snap = await getDoc(ref);
      return {
        success: true,
        conversation: { id: ref.id, _id: ref.id, ...snap.data() },
      };
    } catch (error) {
      console.error('Erreur lors de la création/récupération de la conversation:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la gestion de la conversation',
      };
    }
  },

  /**
   * Obtenir toutes les conversations
   */
  getConversations: async (status) => {
    try {
      if (!firebaseAuth?.currentUser) {
        return { success: false, error: 'Non authentifié', conversations: [] };
      }
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré', conversations: [] };
      }
      const uid = firebaseAuth.currentUser.uid;
      let role = 'client';
      try {
        const prof = await getDoc(doc(firebaseDb, 'users', uid));
        if (prof.exists()) role = prof.data().role || 'client';
      } catch {
        /* ignore */
      }
      const isStaff = role === 'agent' || role === 'admin';

      let q;
      if (isStaff) {
        q = query(collection(firebaseDb, 'conversations'), orderBy('updatedAt', 'desc'), limit(200));
        if (status) {
          q = query(
            collection(firebaseDb, 'conversations'),
            where('status', '==', status),
            orderBy('updatedAt', 'desc'),
            limit(200)
          );
        }
      } else {
        q = query(
          collection(firebaseDb, 'conversations'),
          where('participantUids', 'array-contains', uid),
          orderBy('updatedAt', 'desc')
        );
        if (status) {
          q = query(
            collection(firebaseDb, 'conversations'),
            where('participantUids', 'array-contains', uid),
            where('status', '==', status),
            orderBy('updatedAt', 'desc')
          );
        }
      }
      const snap = await getDocs(q);
      return {
        success: true,
        conversations: snap.docs.map((d) => ({ id: d.id, _id: d.id, ...d.data() })),
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des conversations:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la récupération des conversations',
        conversations: [],
      };
    }
  },

  /**
   * Obtenir les messages d'une conversation
   */
  getMessages: async (conversationId, page = 1, limitSize = 50) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré', messages: [] };
      }
      const q = query(
        collection(firebaseDb, 'conversations', conversationId, 'messages'),
        orderBy('createdAt', 'desc'),
        limit(limitSize)
      );
      const snap = await getDocs(q);
      const messages = snap.docs
        .map((d) => ({ id: d.id, _id: d.id, ...d.data() }))
        .reverse();
      return {
        success: true,
        messages,
        page,
        limit: limitSize,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des messages:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la récupération des messages',
        messages: [],
      };
    }
  },

  /**
   * Envoyer un message (HTTP fallback, mais Socket.IO sera utilisé principalement)
   */
  sendMessage: async (conversationId, content, type = 'text') => {
    try {
      if (!firebaseAuth?.currentUser) {
        return { success: false, error: 'Non authentifié' };
      }
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }

      const uid = firebaseAuth.currentUser.uid;
      const profileSnap = await getDoc(doc(firebaseDb, 'users', uid));
      const profile = profileSnap.exists() ? profileSnap.data() : {};

      const senderPreview = {
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || firebaseAuth.currentUser.email || '',
        role: profile.role || 'client',
      };

      const ref = await addDoc(
        collection(firebaseDb, 'conversations', conversationId, 'messages'),
        {
          senderUid: uid,
          senderPreview,
          content,
          type,
          createdAt: serverTimestamp(),
        }
      );

      await updateDoc(doc(firebaseDb, 'conversations', conversationId), {
        lastMessageAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const snap = await getDoc(ref);
      return {
        success: true,
        message: { id: ref.id, _id: ref.id, ...snap.data() },
      };
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de l\'envoi du message',
      };
    }
  },

  /**
   * Fermer une conversation
   */
  closeConversation: async (conversationId) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré' };
      }
      await updateDoc(doc(firebaseDb, 'conversations', conversationId), {
        status: 'closed',
        updatedAt: serverTimestamp(),
      });
      const snap = await getDoc(doc(firebaseDb, 'conversations', conversationId));
      return {
        success: true,
        conversation: { id: snap.id, _id: snap.id, ...snap.data() },
      };
    } catch (error) {
      console.error('Erreur lors de la fermeture de la conversation:', error);
      return {
        success: false,
        error: error.message || 'Erreur lors de la fermeture de la conversation',
      };
    }
  },
};





